import { SITE_EMAIL } from './site';

export const home = {
  greeting: 'Hi, my name is',
  name: 'Matthew Otto',
  headline: 'I build things for the web...sometimes',
  summary:
    'Full-stack developer specializing in building exceptional digital experiences. Currently focused on building accessible, human-centered products.',
};

export const about = {
  title: 'About Me',
  paragraphs: [
    "I'm a Senior Frontend Engineer with 20+ years of experience building scalable web applications and digital products across startups, agencies, and enterprise organizations. I specialize in modern React, Next.js, TypeScript, and platform architecture, with a focus on creating fast, maintainable systems that support both product growth and long-term engineering scalability.",
    "Throughout my career, I've led engineering initiatives, built products used by millions of users, and helped teams bring ideas from concept to production. My experience spans high-growth startups, venture-backed SaaS platforms, e-commerce, marketing technology, and large-scale consumer applications.",
    "In addition to hands-on engineering, I built and mentored a front-end team of six developers, establishing coding standards, architectural guidelines, and a structured PR review process that improved code quality, streamlined collaboration, and accelerated release cycles. I also created technical documentation, architecture diagrams, and knowledge-sharing resources that simplified onboarding, preserved institutional knowledge, and helped teams scale more effectively.",
    "I often work at the intersection of engineering, product, and design — translating business goals into practical technical solutions, guiding architectural decisions, and helping teams navigate tradeoffs to deliver polished, user-focused products."
  ],
};

export interface ExperienceEntry {
  title: string;
  period: string;
  description: string;
  current?: boolean;
}

export const experience: { title: string; entries: ExperienceEntry[] } = {
  title: 'Experience',
  entries: [
    {
      title: 'Senior Developer',
      period: '2022 \u2013 Present',
      description:
        'Building scalable web applications and leading front-end architecture decisions across multiple product teams.',
      current: true,
    },
    {
      title: 'Full-Stack Developer',
      period: '2019 \u2013 2022',
      description:
        'Developed and maintained client-facing applications using React, Node.js, and PostgreSQL.',
    },
  ],
};

export const contact = {
  title: 'Get in Touch',
  summary:
    "I'm always open to new opportunities and interesting projects. Whether you have a question or just want to say hi, feel free to reach out.",
  email: SITE_EMAIL,
  linkedInUrl: 'https://www.linkedin.com/in/mcotto/',
  githubUrl: 'https://github.com/ottoinfo',
  ctaLabel: 'Say Hello',
};

export interface SideProjectTopic {
  title: string;
  details: string[];
}

export const sideProjects: {
  title: string;
  summary: string;
  topics: SideProjectTopic[];
} = {
  title: 'Side Projects & Interests',
  summary:
    "Outside of day-to-day project work, I spend a lot of time tinkering with systems, tools, and hobbies that keep me curious. If you're into any of these too, we will probably have a lot to talk about.",
  topics: [
    {
      title: "How It Started",
      details: [
        'I was 6 months from graduation with a Bachelor of Fine Arts in Photography when I decided I needed to have a website for my portfolio. With no experience in web development, I decided to teach myself how to code. I started with Dreamweaver and thought flash seemed like a good idea. People then would question me, so you build websites, and I would say "Sure, I can build a website". From there my journey began. I started doing freelance work. I spent the next couple years going to borders and Barnes and Noble and reading every book I could get my hands on.'
      ]
    },
    {
      title: 'Docker',
      details: [
        'I enjoy dialing in local-first setups that are fast to start and easy to share across machines.',
        'A lot of my experiments focus on cleaner compose files, health checks, and startup flows that remove onboarding friction.',
      ],
    },
    {
      title: 'Smart Home Automation | Homelabs',
      details: [
        'Home Assistant & Hubitat',
        'Most of my tinkering is around stable integrations, useful dashboards, and cutting down fragile edge cases.',
      ],
    },
    {
      title: 'Motorcycles',
      details: [
        'I am always interested in the balance between safety, performance, and comfort, especially for longer rides.',
        'I also enjoy the troubleshooting side, from routine maintenance planning to small setup improvements.',
      ],
    },
    {
      title: 'Pool or Billiards',
      details: [
        'I like the strategic side: pattern play, cue ball control, and disciplined fundamentals.',
        'It parallels engineering in a lot of ways: repeatable practice, tight feedback loops, and focus under pressure.',
      ],
    },
    {
      title: 'Gitea and Actions',
      details: [
        'I enjoy experimenting with lightweight self-hosted repos and automation pipelines that are easy to maintain.',
        'Most of this work is about reducing friction: faster checks, cleaner release workflows, and dependable jobs.',
      ],
    },
  ],
};
